import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, User, MapPin } from 'lucide-react';
import { PremiumPageHeader } from '@repo/ui';

type Filter = 'all' | 'upcoming' | 'past';

const SessionsPage = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['student-sessions'],
    queryFn: () => api.get('/student/sessions').then((res) => res.data?.data),
  });

  const filtered = useMemo(() => {
    if (!sessions) return [];
    const now = new Date();
    if (filter === 'upcoming') return sessions.filter((s: any) => new Date(s.startDate) > now);
    if (filter === 'past') return sessions.filter((s: any) => new Date(s.startDate) <= now);
    return sessions;
  }, [sessions, filter]);

  const filterTabs: { key: Filter; label: string }[] = [
    { key: 'all', label: t('student.sessions.all') },
    { key: 'upcoming', label: t('student.sessions.upcoming') },
    { key: 'past', label: t('student.sessions.past') },
  ];

  return (
    <div className="space-y-6 pb-12">
      <PremiumPageHeader
        title={t('student.sessions.title')}
        subtitle={t('student.sessions.subtitle', {
          defaultValue: 'Explore your session history and view upcoming scheduled appointments',
        })}
        icon={Calendar}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      {/* Filter Tabs */}
      <div className="flex items-center gap-4 border-b border-border/60 pb-1 mt-4">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              filter === tab.key
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sessions */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : !filtered.length ? (
        <div className="text-center py-16 text-muted-foreground">
          {t('student.sessions.noSessions')}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((session: any) => {
            const isPast = new Date(session.startDate) <= new Date();
            return (
              <div
                key={session._id}
                className={`rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md ${
                  isPast ? 'opacity-70' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {session.groups?.map((g: any) => g.name).join(', ')}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {new Date(session.startDate).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {new Date(session.startDate).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      {session.host && (
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          {session.host.name}
                        </span>
                      )}
                      {session.branch && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {session.branch.nameEn}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {session.deliveryMode && (
                      <span className="px-3 py-1 text-xs rounded-full bg-primary/10 text-primary font-medium">
                        {session.deliveryMode}
                      </span>
                    )}
                    <span
                      className={`px-2.5 py-1 text-xs rounded-full font-medium ${
                        isPast
                          ? 'bg-muted text-muted-foreground'
                          : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      {isPast ? t('student.sessions.past') : t('student.sessions.upcoming')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SessionsPage;
